import React, { useState, useRef } from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import ChevronLeftIcon from "@atlaskit/icon/core/chevron-left";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { useGlobalNavUserProfileClickActions } from "@/components/global-nav/user-profile-new/src/user-profile";
import { wacToken } from "@/libraries/design/foundations";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { LogoV3 as Logo } from "@/libraries/wpl/foundations/src";

import AutoCompleteSearchBar from "../auto-complete-search-bar";
import { useMobileNavDisplay } from "../hooks/use-mobile-nav-display";
import { SkipToContent } from "../skip-to-content";

import { Divider } from "./icons/divider";
import {
    MobileNavLinks,
    MobileNavLinksContainerInfo
} from "./mobile-nav-links-container";
import MobileLoggedInUseDropdown from "./mobile-nav-logged-in-dropdown";
import { MobileNavTop } from "./mobile-nav-top";
import { MobileProductPageNavDropdowns } from "./mobile-product-page-nav-dropdowns";

const containerCss = css({
    background: wacToken("color.neutral.light0"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.150")
});

const shownInnerMobileDropdownMenuStyle = css({
    display: "block",
    margin: "8px 16px"
});

const hiddenInnerMobileDropdownMenuStyle = css({
    display: "none",
    margin: "8px 16px"
});

const containerClosedCss = css({
    padding: "8px",
    [media.above.sm]: {
        padding: "0"
    }
});

const containerOpenCss = css({
    [media.below.sm]: {
        height: "100vh",
        width: "100%",
        overflow: "auto",
        position: "fixed",
        insetBlockStart: "0",
        padding: "8px 24px 24px 24px",
        zIndex: "5"
    },
    padding: `0`
});

const navBarClosedCss = css({
    paddingInline: token("space.200"),
    [media.above.sm]: {
        paddingInlineStart: "0",
        paddingInlineEnd: token("space.200")
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
});

const navBarOpenCss = css({
    [media.above.sm]: {
        paddingInlineStart: "0",
        paddingInlineEnd: token("space.200")
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
});

const menuContentStyle = css({
    display: "none",
    width: "auto",

    [media.above.sm]: {
        display: "none"
    }
});

const slideInLeft = keyframes({
    from: {
        transform: "translateX(-100%)",
        opacity: "0"
    },
    to: {
        transform: "translateX(0%)",
        opacity: "1"
    }
});

const slideInDown = keyframes({
    from: {
        transform: "translateY(-24px)",
        opacity: "1"
    },
    to: {
        transform: "translateY(24px)",
        opacity: "1"
    }
});

const slideInUp = keyframes({
    from: {
        transform: "translateY(24px)",
        opacity: "1"
    },
    to: {
        transform: "translateY(0px)",
        opacity: "1"
    }
});

const slideInLeftCss = css({
    opacity: "0",
    animation: `0.5s ${slideInLeft} forwards`
});

const slideInUpCss = css({
    opacity: "0",
    animation: `0.5s ${slideInUp} forwards`
});

const slideInDownCss = css({
    opacity: "0",
    animation: `0.5s ${slideInDown} forwards`
});

export type DropdownType = "default" | "search" | "why-bitbucket";

export type MobileGlobalNavProps = {
    productKey: ProductKeyTypes;
    mobileLinksSections: MobileNavLinksContainerInfo[];
    mobileLogo?: JSX.Element;
    mobileLogoAriaLabel?: string;
    mobileLogoHref?: string;
    mobileLogoDataEventContainer?: string;
    isIncludeCTA?: boolean;
    ctaText?: string;
    ctaUrl?: string;
    ctaOnClick?: (e: React.MouseEvent) => void;
    ctaButtonVariant?: "primary" | "secondary";
    isIncludeAtlassianLogo?: boolean;
    isShowTryNowLink?: boolean;
    additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
    isShowSearchButton?: boolean;
    isShowAppSwitcher?: boolean;
    isShowUserProfile?: boolean;
    isShowTabletDropdown?: boolean;
    navType?: "globalNav" | "productNav";
};

export type MobileGlobalNavMenuProps = {
    isDisplayLoggedInUseDropdown: boolean;
};

export type AdditionalMenuPageObjects = {
    menuPageKey: string;
    menuPageObjects: JSX.Element;
};

export type addedMenuPagesObjectType = {
    [key: string]: JSX.Element;
};

const MobileNavProductPageDropdownMenu = ({
    isUserLoggedIn,
    menuPage = "default",
    firstOpen,
    handleMenuPage,
    handleFirstOpen,
    mobileLinksSections,
    productKey,
    isIncludeCTA = true,
    ctaText = "Get it free",
    ctaUrl = "/software/jira/free",
    ctaOnClick,
    ctaButtonVariant = "primary",
    isIncludeAtlassianLogo = true,
    additionalMenuPageObjectsArray = [],
    isShowSearchButton = true
}: {
    readonly isUserLoggedIn: boolean;
    readonly menuPage: DropdownType;
    readonly firstOpen: boolean;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    handleFirstOpen: (firstOpen: boolean) => void;
    readonly mobileLinksSections: MobileNavLinksContainerInfo[];
    readonly productKey: ProductKeyTypes;
    readonly isIncludeCTA?: boolean;
    readonly ctaText?: string;
    readonly ctaUrl?: string;
    readonly ctaOnClick?: (e: React.MouseEvent) => void;
    readonly ctaButtonVariant?: "primary" | "secondary";
    readonly isIncludeAtlassianLogo?: boolean;
    readonly additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
    readonly isShowSearchButton?: boolean;
}) => {
    const MobileNavLinksMenuPage: JSX.Element = (
        <MobileNavLinks
            isLoggedIn={isUserLoggedIn}
            handleMenuPage={handleMenuPage}
            mobileLinksSections={mobileLinksSections}
            productKey={productKey}
            isIncludeCTA={isIncludeCTA}
            ctaText={ctaText}
            ctaUrl={ctaUrl}
            ctaOnClick={ctaOnClick}
            ctaButtonVariant={ctaButtonVariant}
            isIncludeAtlassianLogo={isIncludeAtlassianLogo}
            isShowSearchButton={isShowSearchButton}
        />
    );

    const menuPages: { pageKey: string; content: JSX.Element }[] = [
        { pageKey: "default", content: MobileNavLinksMenuPage }
    ];

    const intl = useIntl();
    const isNavigatingBackRef = useRef(false);

    if (additionalMenuPageObjectsArray.length > 0) {
        for (const additionalMenuPageObject of additionalMenuPageObjectsArray) {
            const { menuPageKey, menuPageObjects } = additionalMenuPageObject;

            menuPages.push({ pageKey: menuPageKey, content: menuPageObjects });
        }
    }

    const { createAnalyticsEvent } = useAnalyticsEvents();

    return (
        <>
            {menuPage === "default" && (
                <div css={[isNavigatingBackRef.current && slideInUpCss]}>
                    <div
                        css={css({
                            position: "relative",
                            width: "100%",
                            paddingInlineStart: "16px",
                            paddingInlineEnd: "16px",
                            paddingBlockStart: "24px"
                        })}
                    >
                        <AutoCompleteSearchBar
                            key="default-search-bar"
                            isMobileView={true}
                            readOnly={true}
                            parentType="ProductNav"
                            menuPage={menuPage}
                            onFocus={() => {
                                if (!isNavigatingBackRef.current) {
                                    handleMenuPage("search");
                                }
                                createAnalyticsEvent({
                                    eventName: "searchOpen",
                                    eventType: "ui",
                                    action: "clicked",
                                    actionSubject: "button",
                                    actionSubjectId: "searchButtonMobileV2",
                                    source: "productNav",
                                    attributes: {
                                        product: "marketing",
                                        pageUrl: window.location.href,
                                        pagePath: window.location.pathname
                                    }
                                }).fire();
                            }}
                        />
                    </div>
                </div>
            )}
            {menuPages.map(({ pageKey, content }, index) => {
                const isDefault = menuPage === "default";
                const isVisible = menuPage === pageKey;

                return (
                    <React.Fragment key={index}>
                        <div
                            key={pageKey}
                            className="inner-mobile-dropdown-menu"
                            css={[
                                isDefault && !firstOpen && slideInUpCss,
                                !isDefault && slideInLeftCss,
                                isVisible
                                    ? shownInnerMobileDropdownMenuStyle
                                    : hiddenInnerMobileDropdownMenuStyle
                            ]}
                        >
                            {content}
                        </div>

                        {/* Dropdowns for navigation items of products */}
                        <MobileProductPageNavDropdowns
                            productKey={productKey}
                            menuPage={menuPage}
                        />
                    </React.Fragment>
                );
            })}
            {menuPage === "search" && (
                <div
                    css={[shownInnerMobileDropdownMenuStyle]}
                    className="searchinnermenu inner-mobile-dropdown-menu"
                >
                    <button
                        style={{ all: "unset", width: "fit-content" }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            isNavigatingBackRef.current = true;
                            handleFirstOpen(false);
                            handleMenuPage("default");

                            // reset the flag after navigation
                            setTimeout(() => {
                                isNavigatingBackRef.current = false;
                            }, 100);
                        }}
                    >
                        <div
                            css={[
                                slideInLeftCss,
                                css({
                                    color: token("color.text.selected"),
                                    display: "flex",
                                    alignItems: "center",
                                    paddingBlockStart: "24px"
                                })
                            ]}
                        >
                            <ChevronLeftIcon label="" spacing="spacious" />
                            <span>
                                {intl.formatMessage({
                                    id: "globalnavv2.menu-back",
                                    defaultMessage: "Back"
                                })}
                            </span>
                        </div>
                    </button>
                    <div
                        css={[
                            slideInDownCss,
                            css({
                                width: "100%",
                                position: "relative"
                            })
                        ]}
                    >
                        <AutoCompleteSearchBar
                            isMobileView={true}
                            parentType="ProductNav"
                            menuPage={menuPage}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

const defaultJiraLogo = (
    <Logo
        brand="jira"
        type="logo"
        appearance="brand"
        height={"25.6px"}
        width={"60px"}
    />
);

export const MobileProductPageNav = ({
    productKey,
    mobileLogo = defaultJiraLogo,
    mobileLogoAriaLabel = "Jira logo",
    mobileLogoHref = "/",
    mobileLogoDataEventContainer = "ProductPageNavJiraV2",
    mobileLinksSections,
    isIncludeCTA = true,
    ctaText = "Get it free",
    ctaUrl = "/software/jira/free",
    ctaOnClick,
    ctaButtonVariant = "primary",
    isIncludeAtlassianLogo = true,
    isShowTryNowLink = true,
    additionalMenuPageObjectsArray = [],
    isShowSearchButton = true,
    isShowAppSwitcher = true,
    isShowUserProfile = true,
    isShowTabletDropdown = true,
    navType = "productNav"
}: // eslint-disable-next-line complexity
MobileGlobalNavProps) => {
    const [isOpenSearch, setIsOpenSearch] = useState(false);
    const intl = useIntl();

    const {
        userInfo,
        showMenu,
        isDisplayLoggedInUseDropdown,
        firstOpen,
        menuPage,
        handleToggleMenu,
        handleFirstOpen,
        handleMenuPage,
        handleClickAvatar
    } = useMobileNavDisplay(isOpenSearch);

    const {
        onSwitchAccountClick,
        onManageProfileClick,
        onManageLicensesClick,
        onLogOutClick
    } = useGlobalNavUserProfileClickActions({ email: userInfo.email });

    const mobileGlobalNavDropdownMenu = () => (
        <MobileNavProductPageDropdownMenu
            isUserLoggedIn={userInfo.isUserLoggedIn}
            menuPage={menuPage as DropdownType}
            firstOpen={firstOpen}
            handleMenuPage={handleMenuPage}
            handleFirstOpen={handleFirstOpen}
            mobileLinksSections={mobileLinksSections}
            productKey={productKey}
            isIncludeCTA={isIncludeCTA}
            ctaText={ctaText}
            ctaButtonVariant={ctaButtonVariant}
            ctaUrl={ctaUrl}
            ctaOnClick={ctaOnClick}
            isIncludeAtlassianLogo={isIncludeAtlassianLogo}
            additionalMenuPageObjectsArray={additionalMenuPageObjectsArray}
            isShowSearchButton={isShowSearchButton}
        />
    );

    return (
        <nav
            aria-label={intl.formatMessage({
                id: "productPageNav.main-nav-label",
                defaultMessage: "Main"
            })}
            css={[
                containerCss,
                showMenu ? containerOpenCss : containerClosedCss
            ]}
        >
            <SkipToContent />
            <div css={[showMenu ? navBarOpenCss : navBarClosedCss]}>
                <MobileNavTop
                    productKey={productKey}
                    isLoggedIn={userInfo.isUserLoggedIn}
                    userName={userInfo.name ?? ""}
                    picture={userInfo.picture ?? ""}
                    handleClickAvatar={handleClickAvatar}
                    showMenu={showMenu}
                    handleToggleMenu={handleToggleMenu}
                    menuPage={menuPage as DropdownType}
                    handleMenuPage={handleMenuPage}
                    handleFirstOpen={handleFirstOpen}
                    mobileLogo={mobileLogo}
                    mobileLogoAriaLabel={mobileLogoAriaLabel}
                    mobileLogoHref={mobileLogoHref}
                    mobileLogoDataEventContainer={mobileLogoDataEventContainer}
                    isOpenSearch={isOpenSearch}
                    setIsOpenSearch={setIsOpenSearch}
                    tabletDropdownMenu={mobileGlobalNavDropdownMenu()}
                    isProductPage={true}
                    isShowTryNowLink={isShowTryNowLink}
                    navigationTrynowCtaText={ctaText}
                    navigationTrynowCtaUrl={ctaUrl}
                    navigationTrynowCtaOnClick={ctaOnClick}
                    navigationTrynowCtaButtonVariant={ctaButtonVariant}
                    isShowSearchButton={isShowSearchButton}
                    isShowAppSwitcher={isShowAppSwitcher}
                    isShowUserProfile={isShowUserProfile}
                    isShowTabletDropdown={isShowTabletDropdown}
                    navType={navType}
                />
            </div>
            <div
                css={[menuContentStyle, showMenu && css({ display: "block" })]}
                id="nav-mobile-dropdown-menu"
            >
                <div>
                    <Divider />
                </div>
                {isDisplayLoggedInUseDropdown ? (
                    <MobileLoggedInUseDropdown
                        userInfo={{
                            displayName: userInfo.name || "",
                            email: userInfo.email || "",
                            avatar: userInfo.picture || ""
                        }}
                        onLogOutClick={onLogOutClick}
                        onManageLicensesClick={onManageLicensesClick}
                        onManageProfileClick={onManageProfileClick}
                        onSwitchAccountClick={onSwitchAccountClick}
                    />
                ) : (
                    mobileGlobalNavDropdownMenu()
                )}
            </div>
        </nav>
    );
};
